import { useQuery } from '@tanstack/react-query';
import { GetFeatureExperimentsResponse } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { useAppSelector } from '../../redux';
import * as UserRedux from '../../redux/user';
import { awaitJSON, UsersLambdaFetch } from '../../utils/fetch-utils';
import { equalsIgnoreCase } from '../../utils/text-utils';

// user_group values (from the experiment_special_users / "User Groups" table) granted write
// access to Feature Experiments, in addition to admins. Matched case-insensitively.
// These are the exact stored group names (econ → econometrics, engineer → engineering). 'admin' is
// not a stored group: useUserGroups reports it for admin-role users so group gates admit admins too.
export const EDITOR_USER_GROUPS = ['econometrics', 'product', 'engineering', 'admin'] as const;

export const useCanEditExperiments = (): boolean => {
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const email = useAppSelector(UserRedux.emailSelector);

  const { data: specialUsers } = useQuery({
    queryKey: ['featureExperiments', 'specialUsers'],
    queryFn: () =>
      UsersLambdaFetch('/getFeatureExperiments')
        .then(res => awaitJSON<GetFeatureExperimentsResponse>(res))
        .then(d => d.special_users),
    enabled: !isAdmin && Boolean(email) // admins already pass; skip the fetch entirely
  });

  if (isAdmin) return true;
  if (!email || !specialUsers) return false;

  const editorGroups = new Set<string>(EDITOR_USER_GROUPS.map(g => g.toLowerCase()));
  return specialUsers.some(
    s => equalsIgnoreCase(s.user, email) && editorGroups.has(s.user_group?.toLowerCase())
  );
};
